import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flame, Loader2, Copy, Check, Send, Globe, Mic, ChevronDown, ChevronUp, X, Download, Disc3, Speaker, ExternalLink } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  auto_generated: { label: "AIRED", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" },
  detected: { label: "DETECTED", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  dismissed: { label: "DISMISSED", cls: "border-line bg-bg-elevated text-ink-muted" },
};

const FILTERS = [
  { id: "", label: "All" },
  { id: "auto_generated", label: "Aired" },
  { id: "detected", label: "Detected" },
  { id: "dismissed", label: "Dismissed" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      data-testid={testid}
      className="shrink-0 rounded-md p-1.5 text-ink-muted hover:text-brand-amber hover:bg-bg-elevated transition"
      title="Copy"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function DownloadBtn({ assetId, testid }) {
  return (
    <a
      href={`${API_BASE}/imaging/assets/${assetId}/audio?download=1`}
      data-testid={testid}
      title="Download MP3 for your playout system"
      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-line px-2 py-1.5 text-[10px] font-semibold text-ink-secondary hover:text-brand-amber hover:border-brand-amber/50 transition"
    >
      <Download className="h-3 w-3" /> MP3
    </a>
  );
}

function TakeCard({ take, onAir, onVoice, onMix, onDismiss, airing, voicing, mixing, focus }) {
  const [open, setOpen] = useState(!!focus);
  const cardRef = React.useRef(null);
  useEffect(() => {
    if (focus && cardRef.current) {
      const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
      return () => clearTimeout(t);
    }
  }, [focus]);
  const meta = STATUS_META[take.status] || STATUS_META.detected;
  const aired = take.status === "auto_generated";
  const socials = take.socials || {};
  const socialEntries = Object.entries(socials).filter(([, v]) => v);
  return (
    <div ref={cardRef} className={`rounded-xl border bg-bg-surface ${focus ? "border-brand-amber ring-1 ring-brand-amber/40" : "border-line"}`} data-testid={`ht-card-${take.hot_take_id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 flex items-start gap-3"
        data-testid={`ht-toggle-${take.hot_take_id}`}
      >
        <div className="shrink-0 h-8 w-8 rounded-lg bg-orange-500/15 text-orange-400 flex items-center justify-center mt-0.5">
          <Flame className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
            <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-300">♫ {take.genre}</span>
            {take.voice_ready && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-300 inline-flex items-center gap-1"><Mic className="h-2.5 w-2.5" /> voice ready</span>}
            {take.wp_published && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 inline-flex items-center gap-1"><Globe className="h-2.5 w-2.5" /> WordPress</span>}
            <span className="font-mono text-[10px] text-ink-muted ml-auto">{timeAgo(take.created_at)}</span>
          </div>
          <div className="text-sm font-semibold text-ink-primary mt-1.5">{take.headline}</div>
          <div className="text-[11px] text-ink-muted mt-0.5">
            {take.source_count} publishers · {(take.sources || []).filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="shrink-0 text-ink-muted mt-1">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</div>
      </button>

      {open && (
        <div className="border-t border-line px-4 py-4 space-y-4" data-testid={`ht-detail-${take.hot_take_id}`}>
          {!aired ? (
            <div className="flex items-center gap-3">
              <p className="text-xs text-ink-secondary flex-1">Not aired yet — one tap generates the on-air script, blog, socials and voice read.</p>
              {take.status !== "dismissed" && (
                <>
                  <button
                    onClick={() => onAir(take.hot_take_id)}
                    disabled={airing === take.hot_take_id}
                    data-testid={`ht-air-${take.hot_take_id}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-bold text-black hover:opacity-90 transition disabled:opacity-50"
                  >
                    {airing === take.hot_take_id ? <><Loader2 className="h-3 w-3 animate-spin" /> Airing…</> : <><Send className="h-3 w-3" /> Air now</>}
                  </button>
                  <button
                    onClick={() => onDismiss(take.hot_take_id)}
                    data-testid={`ht-dismiss-${take.hot_take_id}`}
                    className="rounded-md p-1.5 text-ink-muted hover:text-rose-400 transition" title="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Voice read */}
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-sky-300 mb-1.5">// Voice read (radio 30s)</div>
                {take.audio_asset_id ? (
                  <div className="flex items-center gap-2">
                    <audio
                      controls
                      preload="none"
                      className="flex-1 h-9 min-w-0"
                      src={`${API_BASE}/imaging/assets/${take.audio_asset_id}/audio`}
                      data-testid={`ht-audio-${take.hot_take_id}`}
                    />
                    <DownloadBtn assetId={take.audio_asset_id} testid={`ht-download-${take.hot_take_id}`} />
                  </div>
                ) : (
                  <button
                    onClick={() => onVoice(take.hot_take_id)}
                    disabled={voicing === take.hot_take_id}
                    data-testid={`ht-voice-${take.hot_take_id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-300 hover:bg-sky-500/20 transition disabled:opacity-50"
                  >
                    {voicing === take.hot_take_id ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating…</> : <><Mic className="h-3 w-3" /> Generate voice read</>}
                  </button>
                )}
              </div>

              {/* Anchor mix — opener sting → bed → closer */}
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300 mb-1.5">
                  // Anchor mix — sting · music bed · closer{take.anchor_bed ? ` · ${take.anchor_bed}` : ""}
                </div>
                {take.anchor_asset_id ? (
                  <div className="flex items-center gap-2">
                    <audio
                      controls
                      preload="none"
                      className="flex-1 h-9 min-w-0"
                      src={`${API_BASE}/imaging/assets/${take.anchor_asset_id}/audio`}
                      data-testid={`ht-anchor-audio-${take.hot_take_id}`}
                    />
                    <DownloadBtn assetId={take.anchor_asset_id} testid={`ht-anchor-download-${take.hot_take_id}`} />
                  </div>
                ) : (
                  <button
                    onClick={() => onMix(take.hot_take_id)}
                    disabled={mixing === take.hot_take_id}
                    data-testid={`ht-mix-${take.hot_take_id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
                  >
                    {mixing === take.hot_take_id
                      ? <><Loader2 className="h-3 w-3 animate-spin" /> Mixing…</>
                      : <><Disc3 className="h-3 w-3" /> Create anchor mix</>}
                  </button>
                )}
              </div>

              {/* Script */}
              {take.script_radio_30 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-amber">// On-air script</div>
                    <CopyBtn text={take.script_radio_30} testid={`ht-copy-script-${take.hot_take_id}`} />
                  </div>
                  <div className="rounded-lg border border-line bg-bg-elevated px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-primary whitespace-pre-wrap" data-testid={`ht-script-${take.hot_take_id}`}>
                    {take.script_radio_30}
                  </div>
                </div>
              )}

              {/* Socials */}
              {socialEntries.length > 0 && (
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-violet-300 mb-1.5">// Social posts</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {socialEntries.map(([plat, text]) => (
                      <div key={plat} className="rounded-lg border border-line bg-bg-elevated px-3 py-2 flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{plat}</div>
                          <div className="text-[11.5px] text-ink-secondary mt-0.5 line-clamp-4 whitespace-pre-wrap">{String(text)}</div>
                        </div>
                        <CopyBtn text={String(text)} testid={`ht-copy-${plat}-${take.hot_take_id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const INTRO_KINDS = new Set(["jingle", "opener", "sfx", "fx"]);
const INTRO_VOICE_SUBTYPES = new Set(["dj_drop", "drop", "sweeper", "station_id", "promo", "jingle", "opener"]);

function FlashBriefingCard() {
  const [fb, setFb] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [genning, setGenning] = useState(false);
  const [introOptions, setIntroOptions] = useState([]);

  useEffect(() => {
    api.get("/news-ingestion/flash-briefing").then((r) => setFb(r.data)).catch(() => {});
    api.get("/imaging/assets").then((r) => {
      const opts = (r.data || []).filter((a) =>
        INTRO_KINDS.has(a.kind) || (a.kind === "voice" && INTRO_VOICE_SUBTYPES.has(a.subtype))
      );
      setIntroOptions(opts.slice(0, 100));
    }).catch(() => {});
  }, []);

  const generateNow = async () => {
    setGenning(true);
    const tid = toast.loading("Writing + voicing today's newscast…");
    try {
      await api.post("/news-ingestion/flash-briefing/generate-now");
      const r = await api.get("/news-ingestion/flash-briefing");
      setFb(r.data);
      toast.success("Daily newscast ready — it now leads your briefing feed", { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed", { id: tid });
    } finally { setGenning(false); }
  };

  const save = async (patch) => {
    try {
      const r = await api.put("/news-ingestion/flash-briefing", patch);
      setFb(r.data);
      toast.success(patch.enabled === undefined ? "Flash Briefing settings saved" : (patch.enabled ? "Flash Briefing feed is live" : "Flash Briefing feed disabled"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    }
  };

  if (!fb) return null;
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="flash-briefing-card">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="shrink-0 h-8 w-8 rounded-lg bg-cyan-500/15 text-cyan-400 flex items-center justify-center">
          <Speaker className="h-4 w-4" />
        </div>
        <div className="mr-auto">
          <div className="font-display font-bold text-sm">Alexa Flash Briefing</div>
          <div className="text-[11px] text-ink-muted">"Alexa, what's my Flash Briefing?" — your aired Hot Takes, on every Alexa device.</div>
        </div>
        <button
          onClick={() => save({ enabled: !fb.enabled })}
          data-testid="fb-enable-toggle"
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
            fb.enabled ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-line text-ink-muted hover:text-ink-secondary"
          }`}
        >
          {fb.enabled ? "● Feed live" : "Enable feed"}
        </button>
      </div>

      {fb.enabled && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-[11px] font-mono text-ink-secondary" data-testid="fb-feed-url">
              {fb.feed_url}
            </code>
            <CopyBtn text={fb.feed_url} testid="fb-copy-url" />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex items-center gap-2">
              <span className="text-ink-muted">Items</span>
              <select
                value={fb.max_items}
                onChange={(e) => save({ max_items: Number(e.target.value) })}
                data-testid="fb-max-items"
                className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono focus:border-brand-amber focus:outline-none"
              >
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!fb.prefer_anchor_mix}
                onChange={(e) => save({ prefer_anchor_mix: e.target.checked })}
                data-testid="fb-prefer-anchor"
                className="accent-amber-500"
              />
              <span className="text-ink-muted">Prefer anchor mix (sting + music bed) over plain voice read</span>
            </label>
            <button
              onClick={() => setShowHelp((v) => !v)}
              data-testid="fb-help-toggle"
              className="ml-auto inline-flex items-center gap-1 text-ink-muted hover:text-brand-amber transition"
            >
              How to set up the skill {showHelp ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
          {showHelp && (
            <ol className="list-decimal list-inside space-y-1 rounded-md border border-line bg-bg-elevated p-3 text-[11px] text-ink-secondary" data-testid="fb-help-steps">
              <li>Go to <a href="https://developer.amazon.com/alexa/console/ask" target="_blank" rel="noreferrer" className="text-brand-amber hover:underline inline-flex items-center gap-0.5">developer.amazon.com/alexa/console/ask <ExternalLink className="h-2.5 w-2.5" /></a> (free account) → Create Skill.</li>
              <li>Name it after your station → choose the <b>Flash Briefing</b> model → Create.</li>
              <li>Add a new feed: paste the feed URL above, set Content type to <b>Audio</b>, update frequency <b>Hourly</b>.</li>
              <li>Add a preamble like "Here's the latest from your station" + a 512px icon → Save.</li>
              <li>In the Alexa app on your phone: Skills → Your Skills → Dev → enable it, then say "Alexa, what's my Flash Briefing?"</li>
            </ol>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs border-t border-line pt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!fb.auto_news_enabled}
                onChange={(e) => save({ auto_news_enabled: e.target.checked })}
                data-testid="fb-auto-news-toggle"
                className="accent-amber-500"
              />
              <span className="text-ink-secondary font-semibold">Daily auto-newscast</span>
            </label>
            <label className="flex items-center gap-1.5">
              <span className="text-ink-muted">at</span>
              <select
                value={fb.auto_news_hour ?? 7}
                onChange={(e) => save({ auto_news_hour: Number(e.target.value) })}
                data-testid="fb-auto-news-hour"
                className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono focus:border-brand-amber focus:outline-none"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                ))}
              </select>
              <span className="text-ink-muted">station time</span>
            </label>
            <button
              onClick={generateNow}
              disabled={genning}
              data-testid="fb-generate-now"
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-2.5 py-1 font-semibold hover:bg-brand-amber/20 transition disabled:opacity-50"
            >
              {genning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mic className="h-3 w-3" />}
              {genning ? "Generating…" : "Generate now"}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!fb.podcast_enabled}
                onChange={(e) => save({ podcast_enabled: e.target.checked })}
                data-testid="fb-podcast-toggle"
                className="accent-amber-500"
              />
              <span className="text-ink-muted">Also publish each newscast as a <span className="text-ink-secondary font-semibold">podcast episode</span> + it's always in your <span className="text-ink-secondary font-semibold">Daily Briefing RSS</span> (see RSS Feeds page)</span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-ink-muted">Intro jingle</span>
            <select
              value={fb.intro_asset_id || ""}
              onChange={(e) => save({ intro_asset_id: e.target.value })}
              data-testid="fb-intro-select"
              className="rounded-md border border-line bg-bg-elevated px-2 py-1 font-mono focus:border-brand-amber focus:outline-none max-w-[280px] truncate"
            >
              <option value="">None — voice only</option>
              {introOptions.map((a) => (
                <option key={a.asset_id} value={a.asset_id}>{a.label || a.asset_id} ({a.kind})</option>
              ))}
            </select>
            <span className="text-ink-muted">plays before every newscast (max 20s, auto fade). Make one in Imaging Studio if the list is empty.</span>
          </div>

          {fb.latest_briefing && (
            <div className="rounded-md border border-line bg-bg-elevated p-3 space-y-2" data-testid="fb-latest-briefing">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-ink-primary">{fb.latest_briefing.headline}</span>
                <span className="ml-auto font-mono text-[10px] text-ink-muted">{timeAgo(fb.latest_briefing.created_at)}</span>
              </div>
              {fb.latest_briefing.audio_asset_id && (
                <audio controls preload="none" src={`${API_BASE}/imaging/assets/${fb.latest_briefing.audio_asset_id}/audio`} className="w-full h-8" data-testid="fb-briefing-audio" />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HotTakesArchive() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [airing, setAiring] = useState(null);
  const [voicing, setVoicing] = useState(null);
  const [mixing, setMixing] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("take") || "";

  const load = useCallback(async (status = filter) => {
    try {
      const r = await api.get(`/news-ingestion/hot-takes/archive${status ? `?status=${status}` : ""}`);
      setData(r.data);
    } catch { toast.error("Couldn't load the archive"); }
  }, [filter]);

  useEffect(() => { load(filter); }, [filter, load]);

  const airTake = async (id) => {
    setAiring(id);
    const tid = toast.loading("Airing Hot Take — generating script, socials + voice read…");
    try {
      await api.post(`/news-ingestion/hot-takes/${id}/publish`);
      toast.success("🔥 Aired — script, socials and voice read are ready", { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't air it", { id: tid });
    } finally { setAiring(null); }
  };

  const voiceTake = async (id) => {
    setVoicing(id);
    try {
      await api.post(`/news-ingestion/hot-takes/${id}/voice`, {});
      toast.success("Voice read ready — in your station's chosen voice");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice generation failed");
    } finally { setVoicing(null); }
  };

  const mixTake = async (id) => {
    setMixing(id);
    const tid = toast.loading("Mixing — opener sting, music bed and closer…");
    try {
      const r = await api.post(`/news-ingestion/hot-takes/${id}/anchor-mix`, {});
      toast.success(`Anchor mix ready${r.data.bed ? ` — ${r.data.bed} bed` : ""}`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mix failed", { id: tid });
    } finally { setMixing(null); }
  };

  const dismissTake = async (id) => {
    try { await api.delete(`/news-ingestion/hot-takes/${id}`); load(); }
    catch { toast.error("Couldn't dismiss"); }
  };

  const counts = data?.counts || {};
  const countFor = (id) => (id === "" ? data?.total || 0 : counts[id] || 0);

  return (
    <div className="space-y-5" data-testid="hot-takes-archive-page">
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-10 w-10 rounded-xl bg-orange-500/15 text-orange-400 flex items-center justify-center">
          <Flame className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-xl font-bold text-ink-primary">Hot Takes Archive</h1>
          <p className="text-xs text-ink-secondary mt-0.5">
            Every multi-publisher story your genres caught — with the on-air script, voice read and social posts it generated.
          </p>
        </div>
        <button
          onClick={() => navigate("/dashboard/news-ingestion")}
          data-testid="ht-back-to-news"
          className="ml-auto shrink-0 rounded-md border border-line px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-bg-elevated transition"
        >
          ← News Ingestion
        </button>
      </div>

      <FlashBriefingCard />

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`ht-filter-${f.id || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
              filter === f.id
                ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                : "border-line text-ink-muted hover:text-ink-secondary"
            }`}
          >
            {f.label} <span className="font-mono text-[10px] opacity-70">{countFor(f.id)}</span>
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-ink-muted text-sm py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading archive…
        </div>
      ) : (data.items || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface px-6 py-12 text-center" data-testid="ht-empty">
          <Flame className="h-6 w-6 text-orange-400/60 mx-auto mb-2" />
          <p className="text-sm text-ink-secondary">No Hot Takes here yet.</p>
          <p className="text-xs text-ink-muted mt-1">Pick genres in News Ingestion → Market Settings and the curated feeds will start catching multi-publisher stories.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.items.map((t) => (
            <TakeCard
              key={t.hot_take_id}
              take={t}
              onAir={airTake}
              onVoice={voiceTake}
              onMix={mixTake}
              onDismiss={dismissTake}
              airing={airing}
              voicing={voicing}
              mixing={mixing}
              focus={t.hot_take_id === focusId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
